import { isNil } from 'es-toolkit';

import type { AccountGroupId } from './constants';
import type { AccountGroup } from './types';
import { CLIENT_PORTAL_ALLOCATION_COLORS } from '../common/constants';
import { LIABILITY_GROUP_IDS, SUMMARY_LIABILITY_SWATCH_COLOR } from './constants';

export type AccountsSummaryLine = {
  readonly id: AccountGroupId;
  readonly label: string;
  readonly magnitude: number | null;
  readonly color: string;
  readonly share: number;
};

export type AccountsSummary = {
  readonly assetLines: readonly AccountsSummaryLine[];
  readonly assetsTotal: number;
  readonly liabilityLines: readonly AccountsSummaryLine[];
  readonly liabilitiesTotal: number;
};

function colorAt(index: number) {
  return CLIENT_PORTAL_ALLOCATION_COLORS[index % CLIENT_PORTAL_ALLOCATION_COLORS.length] ?? 'var(--chart-1)';
}

function isLiabilityGroup(groupId: AccountGroupId) {
  return LIABILITY_GROUP_IDS.includes(groupId);
}

function magnitudeOf(total: number | null) {
  return isNil(total) ? null : Math.abs(total);
}

function barShare(total: number | null, half: number) {
  const magnitude = magnitudeOf(total);
  if (isNil(magnitude) || magnitude === 0 || half === 0) {
    return 0;
  }
  return (magnitude / half) * 100;
}

function halfTotal(groups: readonly AccountGroup[]) {
  return Math.abs(groups.reduce((sum, group) => sum + (group.total ?? 0), 0));
}

export function accountsSummary(groups: readonly AccountGroup[]): AccountsSummary {
  const counted = groups.filter((group) => group.total !== 0);
  const assets = counted.filter((group) => !isLiabilityGroup(group.id));
  const liabilities = counted.filter((group) => isLiabilityGroup(group.id));
  const assetsTotal = halfTotal(assets);
  const liabilitiesTotal = halfTotal(liabilities);

  return {
    assetLines: assets.map((group, index) => ({
      id: group.id,
      label: group.label,
      magnitude: magnitudeOf(group.total),
      color: colorAt(index),
      share: barShare(group.total, assetsTotal),
    })),
    assetsTotal,
    liabilityLines: liabilities.map((group) => ({
      id: group.id,
      label: group.label,
      magnitude: magnitudeOf(group.total),
      color: SUMMARY_LIABILITY_SWATCH_COLOR,
      share: barShare(group.total, liabilitiesTotal),
    })),
    liabilitiesTotal,
  };
}

export function hasSummaryLines(summary: AccountsSummary) {
  return summary.assetLines.length > 0 || summary.liabilityLines.length > 0;
}
